import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useLocation } from "react-router-dom";
// @ts-ignore
import { DateTime } from 'luxon';
// @ts-ignore
import { EnsureAccount } from 'components/customer/accounts/EnsureAccount';
import MyUsageChart from "./MyUsageChart";
import { useMyTimezone } from 'components/customer/orgs/MyOrgAPIEndpoints';
import useAccount from '../../lib/useAccount';
import { getAccountPopoverTitle } from '../../lib/AccountProvider';
// @ts-ignore
import { useResponsive } from '../../lib/useResponsive';
import { useTranslation } from 'react-i18next';
import { UcWidget } from '../common/UcWidget';
import useMyUsageActions from './MyUsageActions';
import { USAGE_FILTER_TYPES, USAGE_HEADERS, USAGE_GENERATION_HEADERS } from './MyUsage.constants';
import { UsageHelpers } from './MyUsage.helpers';
// @ts-ignore
import { dateRangeFormatterV3, commodityToUom, ensureLuxonDateUtcToTimezone } from '../../lib/Formatters';
import MyUsageFilterDialog from './MyUsageFilterDialog';
import { useMyUsageChartData } from './MyUsageApi';
import useOrgAppSettings from '../../lib/useOrgAppSettings';
import { UcSummaryTable } from '../common/UcSummaryTable';
import { useMyWeatherChartData } from './MyWeatherChartApi';
import { addWeatherData } from './MyUsagePageWeatherData';
import { UcLoading } from '../common/UcLoading';
import { UcProgress } from '../common/UcProgress';
import { useMyOrgs } from "../customer/orgs/MyOrgAPIEndpoints";
// @ts-ignore
import { useReadingsGB } from '../readings/ReadingAPIEndpoint';
// @ts-ignore
import { saveAs } from 'file-saver';
import { UcProgressWrapper } from './UcProgressWrapper';
import { PageContext, PageState, PageAction } from '../../lib/PageProvider';
import { Alert, Stack, IconButton, Typography } from '@mui/material';
// @ts-ignore
import { useHasModule } from "components/auth/PermissionApiEndpoint";
import MyUsageGenerationChart from './MyUsageGenerationChart';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useMeterAttributes } from '../udmData/MeterAttributeAPIEndpoints';

function MyUsagePage() {
    // Hooks
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { account } = useAccount();
    const timezone = useMyTimezone();
    const location = useLocation();
    const popoverTitle = getAccountPopoverTitle(account);
    const { orgAppSettings } = useOrgAppSettings();
    const swrMyOrg = useMyOrgs();
    const hasCostModule = useHasModule("Cost");

    const yesterday = useMemo(() => DateTime.now().minus({ days: 1 }), []);

    // States
    const [chartHeader, setChartHeader] = useState(USAGE_HEADERS.DAY);
    const [chartGenerationHeader, setChartGenerationHeader] = useState(USAGE_GENERATION_HEADERS.DAY);
    const [meterShowGenerationGraph, setMeterShowGenerationGraph] = useState<boolean>(true);
    const [chartType, setChartType] = useState(USAGE_FILTER_TYPES.DAY);
    const [uom, setUom] = useState(location.state?.locUom || 'KWH');

    const [yearStart, setYearStart] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, 1, timezone, 'yearStart'));
    // yearEnd -24 hours to ensure we don't pull an extra billing cycle we don't need.. the backend handles getting the correct end date based on bill cycle
    const [yearEnd, setYearEnd] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'yearEnd', -24));

    const [monthStart, setMonthStart] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'monthStart'));
    const [monthEnd, setMonthEnd] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'monthEnd'));

    const [hourStart, setHourStart] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'hourStart', 0));
    const [hourEnd, setHourEnd] = useState(UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'hourEnd'));

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [pageState, pageDispatch] = useContext<[PageState, React.Dispatch<PageAction>]>(PageContext);
    const isInitialMount = useRef(true);

    useEffect(() => { // Making sure we use proper timezone on first render
        if ((isInitialMount.current || timezone) && (chartType === USAGE_FILTER_TYPES.DAY)) { 
            const timezoneStartDate = UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'hourStart', 0);
            const timezoneEndDate = UsageHelpers.createDateFromObject(yesterday.year, yesterday.month, yesterday.day, timezone, 'hourEnd');

            setStartDate(timezoneStartDate);
            setEndDate(timezoneEndDate);
            setHourStart(timezoneStartDate);
            setHourEnd(timezoneEndDate);

            isInitialMount.current = false;
        }
    }, [timezone, yesterday, chartType]);

    const [groupBy, setGroupBy] = useState("none");
    const [progress, setProgress] = useState({ current: 0, total: 100 });
    const [meterOptionsLoaded, setMeterOptionsLoaded] = useState<boolean>(false);

    const [serialNo, setSerialNo] = useState<string | null>("none");

    const [csvDataToDownload, setCsvDataToDownload] = useState([]);
    const [csvGenerationDataToDownload, setCsvGenerationDataToDownload] = React.useState([]);

    const [isDownloadReady, setIsDownloadReady] = useState<boolean>(false);

    let uomDisplay = orgAppSettings?.result?.uomMappings[uom.toUpperCase()]?.safeUomDisplay;

    // FOR CHART ONLY
    const [geoCoordinates, setGeoCoordinates] = useState(UsageHelpers.getGeoCoordinates(account, uom) || []);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const accountIdRef = React.useRef(account?.id);
    useEffect(() => {
        accountIdRef.current = account?.id; // Used to fix the extra api request when account updates too quickly
    }, [account?.id]);

    const usageChartDataFull = useMyUsageChartData(accountIdRef.current, groupBy, uom, startDate, endDate, serialNo);
    const usageChartData = usageChartDataFull?.result?.delivered;
    const usageChartDataReceived = usageChartDataFull?.result?.received;

    const weatherChartData = useMyWeatherChartData(startDate, endDate, groupBy, geoCoordinates);
    const swrGBReadings = useReadingsGB(startDate, endDate, groupBy, uom, account?.id, isDownloadReady);

    const meterAttributes = useMeterAttributes(accountIdRef.current, serialNo, uom);

    let chartDataPoints = usageChartData?.dataPoints;
    chartDataPoints = addWeatherData(chartDataPoints, weatherChartData, groupBy);

    let chartDataPointsReceived = usageChartDataReceived?.dataPoints;

    const onChangeOpenFilterDialog = (value: boolean) => {
        setOpenFilterDialog(value);
    }

    useEffect(() => {
        if (chartDataPoints?.length > 0) {
            setCsvDataToDownload(chartDataPoints);
        }
    }, [chartDataPoints])

    useEffect(() => {
        if (chartDataPointsReceived?.length > 0) {
            setCsvGenerationDataToDownload(chartDataPointsReceived);
        }
    }, [chartDataPointsReceived])

    let orgTempUnit: string | null = null;
    let precipUnit: string | null = null;
    if (!swrMyOrg.isLoading && swrMyOrg.result?.length > 0) {
        orgTempUnit = swrMyOrg.result[0].temperatureUnit;
        precipUnit = (orgTempUnit === "F") ? t("in.") : t("mm");
    }

    const { actionItems, generationActionItems, getFileName, meterOptions } = useMyUsageActions({
        startDate,
        endDate,
        uom,
        csvDataToDownload,
        csvGenerationDataToDownload,
        setIsDownloadReady,
        orgTempUnit,
        hasCostModule,
        parentHandleOnChangeChartType: (value: string) => {
            switch (value) {
                case USAGE_FILTER_TYPES.YEAR:
                    setChartHeader(USAGE_HEADERS.YEAR);
                    setChartGenerationHeader(USAGE_GENERATION_HEADERS.YEAR);
                    setStartDate(yearStart);
                    setEndDate(yearEnd);
                    setGroupBy("month");

                    break;
                case USAGE_FILTER_TYPES.MONTH:
                    setChartHeader(USAGE_HEADERS.MONTH);
                    setChartGenerationHeader(USAGE_GENERATION_HEADERS.MONTH);
                    setStartDate(monthStart);
                    setEndDate(monthEnd);
                    setGroupBy("day");

                    break;
                case USAGE_FILTER_TYPES.DAY:
                    setChartHeader(USAGE_HEADERS.DAY);
                    setChartGenerationHeader(USAGE_GENERATION_HEADERS.DAY);
                    setStartDate(hourStart);
                    setEndDate(hourEnd);
                    setGroupBy("none");

                    break;
            }

            setChartType(value);
        },
        parentHandleOnChangeSelectedMeter: (option: any) => {
            setSerialNo(option.serialNo);
            setMeterShowGenerationGraph(option.showGeneration);
            setUom(commodityToUom(option.meterCommodity));
            setGeoCoordinates(UsageHelpers.getGeoCoordinates(account, commodityToUom(option.meterCommodity)));
        },
        parentHandleOpenFilterDialog: () => setOpenFilterDialog(true)
    });

    if (isDownloadReady && swrGBReadings.result) {
        let xmlResult = swrGBReadings.result;
        let blob = new Blob([xmlResult], { type: "text/plain;charset=utf-8" });
        saveAs(blob, getFileName("xml"));
        setIsDownloadReady(false);
    }

    let defaultValues = {
        accountId: account?.id ?? 0,
        serialNo: "",
        uom: "KWH",
        uomName: "KWH",
        startDate: startDate,
        endDate: endDate,
        endingOn: startDate ?? yesterday,
        endingOnYearly: startDate ?? yesterday,
        endingOnMonthly: ""
    }

    useEffect(() => {
        pageDispatch({
            header: t(""),
            actionItems: [] as any[],
            tabs: [],
            onTabChange: () => { },
            tabValue: ''
        });
    }, [pageState.header, pageDispatch, t]);

    useEffect(() => {
        if (account?.id && uom && startDate && endDate) {
            setMeterOptionsLoaded(true);
        }
    }, [account, uom, startDate, endDate, meterOptions]);

    const latestMeterAttribute = meterAttributes?.result?.reduce((max: any, current: any) => {
        return new Date(current.lastReadTs) > new Date(max.lastReadTs) ? current : max;
    });

    const latestDateTimezone = latestMeterAttribute?.lastReadTs
        ? ensureLuxonDateUtcToTimezone(latestMeterAttribute?.lastReadTs, timezone)
        : null;
    const latestDateStringTimezone = latestDateTimezone ? latestDateTimezone.toFormat("MMMM dd, yyyy 'at' t") : "";

    const { dateRangeStart, dateRangeEnd } = UsageHelpers.calculateDateRangeText(chartType, usageChartData, startDate, endDate, timezone);

    return (
        <EnsureAccount>
            <MyUsageFilterDialog
                serialNo={serialNo}
                chartType={chartType}
                defaultValues={defaultValues}
                uom={uom}
                startDate={startDate}
                endDate={endDate}
                open={openFilterDialog}
                onChangeOpen={onChangeOpenFilterDialog}
                onSubmit={(values: any) => {
                    if (chartType === USAGE_FILTER_TYPES.DAY) {
                        setHourStart(values.startDate);
                        setHourEnd(values.endDate);
                    }
                    else if (chartType === USAGE_FILTER_TYPES.YEAR) {
                        setYearStart(values.startDate);
                        setYearEnd(values.endDate);
                    }
                    else if (chartType === USAGE_FILTER_TYPES.MONTH) {
                        setMonthStart(values.startDate);
                        setMonthEnd(values.endDate);
                    }

                    setStartDate(values.startDate);
                    setEndDate(values.endDate);
                }}
            />
            <UcWidget
                title={chartHeader + " - " + account?.accountAddress}
                subTitle={t("Last Meter Read: ") + latestDateStringTimezone}
                popoverTitle={popoverTitle}
                actionItems={actionItems}
                contentPadding={'0 0 0 0'}
                alignTitleLeft={true}
                height={"100%"}
            >
                <UcProgressWrapper
                    data={usageChartData}
                    selectedMeter={serialNo}
                    groupBy={groupBy}
                    readyForData={true}
                    onProgress={(progressLocal: any) => {
                        if (progressLocal.current !== progress.current) {
                            setProgress({ ...progressLocal });
                        }
                    }}
                />
                <UcLoading hasError={false} hasFade={false} isLoading={usageChartDataFull.isLoading} loadingRender={<UcProgress {...progress} />}>
                    {(meterOptionsLoaded && !usageChartDataFull.isLoading) && (
                        <>
                            {meterOptions?.length > 0 ? (
                                <>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ width: '100%', px: 2 }}
                                    >
                                        <IconButton 
                                            title={t("Back")}
                                            sx={{ color: 'primary.main' }}
                                            data-cy-id={"myUsagePage-backArrow"}
                                            onClick={() => {
                                                if (!startDate || !endDate) return;
                                                const newStartDate = UsageHelpers.navigateDate(startDate, chartType, 'backward', timezone);
                                                const newEndDate = UsageHelpers.navigateDate(endDate, chartType, 'backward', timezone);

                                                if (chartType === USAGE_FILTER_TYPES.DAY) {
                                                    setHourStart(newStartDate);
                                                    setHourEnd(newEndDate);
                                                } else if (chartType === USAGE_FILTER_TYPES.MONTH) {
                                                    setMonthStart(newStartDate);
                                                    setMonthEnd(newEndDate);
                                                } else if (chartType === USAGE_FILTER_TYPES.YEAR) {
                                                    setYearStart(newStartDate);
                                                    setYearEnd(newEndDate);
                                                }
                                                
                                                setStartDate(newStartDate);
                                                setEndDate(newEndDate);
                                            }}
                                            disabled={!UsageHelpers.isNavigationAllowed(startDate, 'backward', account?.minStartTimestamp)}
                                        >
                                            <ArrowBackIosIcon />
                                        </IconButton>
                                        <Typography align="center" fontWeight="bold" title={dateRangeFormatterV3(dateRangeStart, dateRangeEnd, timezone, isMobile && uom)}>
                                            {dateRangeFormatterV3(dateRangeStart, dateRangeEnd, timezone, isMobile && uom)}
                                        </Typography>
                                        <IconButton
                                            title={t("Forward")}
                                            sx={{ color: 'primary.main' }}
                                            data-cy-id={"myUsagePage-forwardArrow"}
                                            onClick={() => {
                                                if (!startDate || !endDate) return;
                                                const newStartDate = UsageHelpers.navigateDate(startDate, chartType, 'forward', timezone);
                                                const newEndDate = UsageHelpers.navigateDate(endDate, chartType, 'forward', timezone);

                                                if (chartType === USAGE_FILTER_TYPES.DAY) {
                                                    setHourStart(newStartDate);
                                                    setHourEnd(newEndDate);
                                                } else if (chartType === USAGE_FILTER_TYPES.MONTH) {
                                                    setMonthStart(newStartDate);
                                                    setMonthEnd(newEndDate);
                                                } else if (chartType === USAGE_FILTER_TYPES.YEAR) {
                                                    setYearStart(newStartDate);
                                                    setYearEnd(newEndDate);
                                                }
                                                
                                                setStartDate(newStartDate);
                                                setEndDate(newEndDate);
                                            }}
                                            disabled={!UsageHelpers.isNavigationAllowed(endDate, 'forward', undefined, account?.maxEndTimestamp)}
                                        >
                                            <ArrowForwardIosIcon />
                                        </IconButton>
                                    </Stack>
                                    <MyUsageChart
                                        data={usageChartData}
                                        dataPoints={chartDataPoints}
                                        uomDisplay={uomDisplay}
                                        uom={uom}
                                        groupBy={groupBy}
                                        orgTempUnit={orgTempUnit}
                                        precipUnit={precipUnit}
                                        hasCostModule={hasCostModule}
                                    />
                                </>
                               
                            ) : (
                                <Alert severity="warning" sx={{ mx: 5, mt: 10 }}>{t("No meter options are available to display data.")}</Alert>
                            )}
                        </>
                    )}
                </UcLoading>
            </UcWidget>
            <br />
            <UcSummaryTable
                rateCategories={usageChartData?.rateDescriptions}
                uom={orgAppSettings?.result?.uomMappings[uom]?.safeUomDisplay}
            />
            <br />
            {meterShowGenerationGraph && usageChartDataReceived && (Object.keys(usageChartDataReceived)?.length > 0) && (
                <UcWidget
                    title={chartGenerationHeader}
                    actionItems={generationActionItems}
                    hideTitlesOnMobile={false}
                    alignTitleLeft={true}
                    height={"100%"}
                    overflowY={"hidden"}
                >
                    <MyUsageGenerationChart
                        dataPoints={chartDataPointsReceived}
                        uom={uom}
                        uomDisplay={uomDisplay}
                        groupBy={groupBy}
                    />
                </UcWidget>
            )}
        </EnsureAccount>
    );
}

export default MyUsagePage;